import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Plus, Loader2, Truck, Package, ShieldCheck, Sparkles } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { SALON_INTERIOR_COTRIL, COTRIL_BRAND_HERO } from "@/data/gallery";
import Reveal from "@/components/Reveal";
import TiltCard from "@/components/TiltCard";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (n) => new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(n);

const Loja = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState("Todos");
  const { addItem } = useCart();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/products`);
        setProducts(res.data);
      } catch (e) {
        console.error("Failed to load products", e);
        toast.error("Não foi possível carregar os produtos.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const categories = useMemo(() => {
    const cats = [...new Set(products.map((p) => p.category))].sort();
    return ["Todos", ...cats];
  }, [products]);

  const filtered = useMemo(
    () => (activeCat === "Todos" ? products : products.filter((p) => p.category === activeCat)),
    [activeCat, products]
  );

  const handleAdd = (p) => {
    addItem(p, 1);
    toast.success(`${p.name} adicionado ao carrinho`);
  };

  return (
    <div data-testid="loja-page" className="pb-24 overflow-x-clip">
      {/* ===== COMPACT HERO ===== */}
      <section className="relative h-[42vh] min-h-[320px] -mt-24 overflow-hidden flex items-end bg-ink">
        <img
          src={SALON_INTERIOR_COTRIL}
          alt="Linha Cotril no estúdio Tamara Alves"
          className="absolute inset-0 w-full h-full object-cover ken-burns"
          style={{ objectPosition: "center 75%" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/55 to-ink/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />

        <span aria-hidden className="absolute top-20 right-12 font-serif italic text-[14rem] leading-none gold-text opacity-20 select-none pointer-events-none hidden lg:block">
          C
        </span>

        <div className="container-luxe relative z-10 pb-10 pt-32">
          <Reveal>
            <p className="overline mb-4 !text-gold">— Pre-Shop · Cotril Professional</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[0.95] text-beige max-w-3xl">
              Cuidado <span className="italic gold-text">profissional</span>.
            </h1>
            <p className="mt-4 text-beige/80 max-w-xl">
              Os mesmos produtos que utilizo no salão.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ===== TRUST STRIP (compact) ===== */}
      <section className="bg-ink text-beige border-b border-gold/20">
        <div className="container-luxe py-5 flex flex-wrap items-center justify-center lg:justify-between gap-x-10 gap-y-3 text-xs">
          <span className="flex items-center gap-2"><Truck className="w-4 h-4 text-gold" strokeWidth={1.5} /> Envios em 24h úteis</span>
          <span className="flex items-center gap-2"><Package className="w-4 h-4 text-gold" strokeWidth={1.5} /> Portes grátis acima de 45€</span>
          <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-gold" strokeWidth={1.5} /> Produtos originais Cotril</span>
        </div>
      </section>

      {/* ===== INTRO (compact) ===== */}
      <section className="container-luxe pt-10 pb-6">
        <Reveal>
          <p className="text-ink/70 max-w-2xl leading-relaxed">
            Cada produto da loja é testado e usado diariamente no salão. Filtre por categoria
            para encontrar o que precisa.
          </p>
        </Reveal>
      </section>

      <section className="container-luxe">
        <div className="flex flex-wrap gap-2 mb-10 border-b border-gold/15 pb-6" data-testid="loja-filters">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActiveCat(c)}
              data-testid={`loja-filter-${c.toLowerCase()}`}
              className={`px-5 py-2 text-xs uppercase tracking-[0.2em] transition-all border ${
                activeCat === c
                  ? "bg-gold-bright border-gold text-ink shadow-[0_0_15px_rgba(255,215,0,0.35)]"
                  : "border-ink/15 text-ink/70 hover:border-gold hover:text-gold"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-24 flex justify-center" data-testid="loja-loading">
            <Loader2 className="w-8 h-8 text-gold animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="py-24 text-center text-ink/50">Sem produtos nesta categoria.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filtered.map((p) => (
              <TiltCard
                key={p.id}
                maxTilt={5}
                className="bg-beige-light flex flex-col group relative"
              >
                <article
                  data-testid={`product-card-${p.id}`}
                  className="flex flex-col h-full"
                >
                <div className="aspect-square bg-beige overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <p className="overline mb-2">{p.line}</p>
                  <h3 className="font-serif text-xl text-ink leading-snug mb-1">{p.name}</h3>
                  <p className="text-xs text-ink/50">{p.size}</p>
                  <p className="text-xs text-ink/60 mt-3 line-clamp-2 flex-1">{p.description}</p>
                  <div className="mt-5 flex items-end justify-between">
                    <span className="font-serif text-2xl text-gold tabular-nums" data-testid={`product-price-${p.id}`}>
                      {fmt(p.price)}
                    </span>
                    <button
                      onClick={() => handleAdd(p)}
                      data-testid={`add-to-cart-${p.id}`}
                      className="inline-flex items-center gap-2 bg-ink text-beige px-4 py-2.5 text-xs uppercase tracking-[0.18em] hover:bg-gold hover:text-ink transition-colors duration-300"
                    >
                      <Plus className="w-3.5 h-3.5" /> Adicionar
                    </button>
                  </div>
                </div>
                </article>
              </TiltCard>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Loja;
